// back-planning.ts — quote back-planning. Pure module.
//
// Three dates counted back from delivery: when files must arrive, when proofs must be approved, and
// when we deliver. Sales fills them in; the engine only proposes defaults and reports what is missing.
//
// Working days include French public holidays, Easter computed (it drives Easter Monday, Ascension
// and Whit Monday): ignoring them would silently produce unreachable dates around long weekends.
//
// No call to `new Date()` without argument here. Every date is passed in, so results are testable.

import { engineSettings, WORKSHOP_LEAD_TIMES } from "../../data";
import { engineMessage } from "../core/messages";

/** A short ISO date, "2026-08-07", as carried by the database `date` columns. */
export type DateISO = string;

/** The back-planning dates. All required to freeze a quote (see `missingBackPlanningItems`). */
export interface BackPlanning {
  /** When the client's files must be received. Default: 4 working days before delivery. */
  fileReception?: DateISO | null;
  /** When all proofs must be approved. Default: 3 working days before delivery. */
  validationBat?: DateISO | null;
  /**
   * The delivery date is deliberately absent: it lives in the `devis.date_livr` column, read by the
   * folder, the dashboard and the production sub-lines. Copying it here would create two truths.
   */
  /** Multiple deliveries: opens the per-line date column in the form. */
  multiDelivery?: boolean;
}

/**
 * Default offsets in working days before delivery, from the settings (`quote_engine.back_planning`).
 * @returns The file-reception and proof-approval offsets.
 */
export function backPlanningDefaults(): { fileReceptionDays: number; batValidationDays: number } {
  const { fileReceptionDays, batValidationDays } = engineSettings().backPlanning;
  return { fileReceptionDays, batValidationDays };
}

/**
 * A workshop with a long production lead time. The lead time is a column of the workshops table
 * (`quote_workshops.lead_time_business_days`, counted in working days like the other two dates), so a
 * second constrained workshop only needs a value in that column.
 */
export interface LongLeadWorkshop {
  /** Short name of the workshop (label without the contact in brackets), capitalised for display. */
  workshop: string;
  /** Working days before delivery by which the files must be supplied. */
  days: number;
}

// Working days

/**
 * Parses an ISO date as a UTC midnight Date.
 * @param isoDate The ISO date, "YYYY-MM-DD".
 * @returns The UTC date.
 */
const parseDay = (isoDate: DateISO): Date => {
  const [y, m, d] = isoDate.split("-").map(Number);
  return new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1));
};

/**
 * Formats a Date as a short ISO date.
 * @param d The date.
 * @returns The "YYYY-MM-DD" string.
 */
const toIso = (d: Date): DateISO => d.toISOString().slice(0, 10);

/**
 * Gregorian Easter Sunday (Butcher's algorithm). It drives three of the eleven holidays.
 * @param year The year.
 * @returns Easter Sunday, at UTC midnight.
 */
export function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
}

/**
 * The eleven French public holidays of a year, in ISO form.
 * @param year The year.
 * @returns The holidays as ISO dates.
 */
export function frenchPublicHolidays(year: number): DateISO[] {
  const easter = easterSunday(year);
  /**
   * ISO date of Easter shifted by a number of days.
   * @param n The shift in days.
   * @returns The ISO date.
   */
  const shifted = (n: number) => toIso(new Date(easter.getTime() + n * 86400000));
  /**
   * ISO date of a fixed-date holiday.
   * @param m The month (1-12).
   * @param d The day of the month.
   * @returns The ISO date.
   */
  const fixed = (m: number, d: number) => toIso(new Date(Date.UTC(year, m - 1, d)));
  return [
    fixed(1, 1),      // New Year's Day
    shifted(1),       // Easter Monday
    fixed(5, 1),      // Labour Day
    fixed(5, 8),      // Victory 1945
    shifted(39),      // Ascension
    shifted(50),      // Whit Monday
    fixed(7, 14),     // Bastille Day
    fixed(8, 15),     // Assumption
    fixed(11, 1),     // All Saints
    fixed(11, 11),    // Armistice 1918
    fixed(12, 25),    // Christmas
  ];
}

/**
 * A working day: not Saturday, Sunday or a public holiday.
 * @param day The ISO date to test.
 * @returns True when the day is a working day.
 */
export function isWorkingDay(day: DateISO): boolean {
  const date = parseDay(day);
  const weekday = date.getUTCDay();
  if (weekday === 0 || weekday === 6) return false;
  return !frenchPublicHolidays(date.getUTCFullYear()).includes(day);
}

/**
 * Steps back `n` working days from `fromDate`. `n = 0` returns the date itself, even on a Sunday.
 * Guarded at 400 iterations so an absurd input cannot loop forever.
 * @param fromDate The ISO date to count back from.
 * @param n The number of working days to step back.
 * @returns The resulting ISO date, or null when the input is invalid.
 */
export function subtractWorkingDays(fromDate: DateISO, n: number): DateISO | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(fromDate) || !Number.isFinite(n) || n < 0) return null;
  let d = parseDay(fromDate);
  if (Number.isNaN(d.getTime())) return null;
  let remaining = Math.floor(n);
  let guard = 0;
  while (remaining > 0 && guard++ < 400) {
    d = new Date(d.getTime() - 86400000);
    if (isWorkingDay(toIso(d))) remaining--;
  }
  return remaining === 0 ? toIso(d) : null;
}

/**
 * Working days between file reception and delivery, as used on the PDF (« Délai : N jours ouvrés »).
 * Inverse of {@link subtractWorkingDays}: finds `n` such that stepping back `n` working days from
 * `deliveryDate` lands on `fileReception`.
 * @param fileReception When files must be received (ISO day).
 * @param deliveryDate Contractual delivery date (ISO day).
 * @returns The offset in working days, or null when the pair is invalid or inconsistent.
 */
export function fileReceptionLeadDays(
  fileReception: DateISO | null | undefined,
  deliveryDate: DateISO | null | undefined,
): number | null {
  const from = String(fileReception ?? "").trim();
  const to = String(deliveryDate ?? "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || !/^\d{4}-\d{2}-\d{2}$/.test(to)) return null;
  for (let n = 0; n <= 400; n++) {
    if (subtractWorkingDays(to, n) === from) return n;
  }
  return null;
}

// The back-planning itself

/**
 * The two dates proposed from the delivery date. All `null` when delivery is unknown.
 * @param deliveryDate The delivery date, ISO.
 * @returns The proposed file-reception and proof-approval dates.
 */
export function defaultBackPlanning(deliveryDate: DateISO | null | undefined): {
  fileReception: DateISO | null;
  validationBat: DateISO | null;
} {
  if (!deliveryDate) return { fileReception: null, validationBat: null };
  const { fileReceptionDays, batValidationDays } = backPlanningDefaults();
  return {
    fileReception: subtractWorkingDays(deliveryDate, fileReceptionDays),
    validationBat: subtractWorkingDays(deliveryDate, batValidationDays),
  };
}

/**
 * Short name of a workshop label: the part before the contact in brackets, uppercased.
 * @param label The workshop label ("LABOPRINT (Alex)").
 * @returns The match key ("LABOPRINT").
 */
function workshopBaseName(label: string): string {
  return label.replace(/\s*\(.*$/, "").trim().toUpperCase();
}

/**
 * The most constraining long-lead workshop producing something on this quote, if any.
 * A resolved workshop matches a configured one when it contains its short name (case-insensitive).
 * @param workshops The resolved workshop of each line.
 * @returns The workshop and its lead time in working days, or null when none is long-lead.
 */
export function longLeadWorkshop(workshops: readonly (string | null | undefined)[]): LongLeadWorkshop | null {
  let found: LongLeadWorkshop | null = null;
  for (const [label, days] of Object.entries(WORKSHOP_LEAD_TIMES)) {
    const key = workshopBaseName(label);
    if (!key || !workshops.some((w) => (w ?? "").toUpperCase().includes(key))) continue;
    if (!found || days > found.days) found = { workshop: key.charAt(0) + key.slice(1).toLowerCase(), days };
  }
  return found;
}

/**
 * The dedicated back-planning added when a long-lead workshop produces a quote line.
 * @param deliveryDate The delivery date, ISO.
 * @param leadDays The lead time of the workshop, in working days.
 * @returns The file-reception date and the delivery date.
 */
export function longLeadBackPlanning(
  deliveryDate: DateISO | null | undefined,
  leadDays: number,
): {
  fileReception: DateISO | null;
  deliveryDate: DateISO | null;
} {
  if (!deliveryDate) return { fileReception: null, deliveryDate: null };
  return {
    fileReception: subtractWorkingDays(deliveryDate, leadDays),
    deliveryDate,
  };
}

/**
 * The alert shown when a long-lead workshop produces on the quote (an encoded message: key + workshop + days).
 * @param lead The long-lead workshop.
 * @returns The encoded message to translate with `resolveEngineMessage`.
 */
export function longLeadAlert(lead: LongLeadWorkshop): string {
  return engineMessage("engineLongLeadAlert", { workshop: lead.workshop, days: lead.days });
}

/** One missing back-planning item, with the i18n key of its message. */
export interface MissingBackPlanning {
  field: "fileReception" | "validationBat" | "deliveryDate";
  messageKey: string;
}

/**
 * What is missing to freeze. Back-planning is mandatory and the block cannot be overridden, like
 * payment terms. It blocks at freeze time, not later, so a frozen quote never ends up stuck.
 * A whitespace-only date counts as empty.
 * @param r The back-planning dates.
 * @param deliveryDate The quote's delivery date.
 * @returns The missing items (message keys, translated by the display layer).
 */
export function missingBackPlanningItems(
  r: BackPlanning | null | undefined,
  deliveryDate: DateISO | null | undefined,
): MissingBackPlanning[] {
  /**
   * Is the value empty once trimmed?
   * @param v The value to test.
   * @returns True when empty.
   */
  const isBlank = (v: unknown) => String(v ?? "").trim() === "";
  const out: MissingBackPlanning[] = [];
  if (isBlank(deliveryDate)) out.push({ field: "deliveryDate", messageKey: "engineBackPlanMissingDelivery" });
  if (isBlank(r?.fileReception)) out.push({ field: "fileReception", messageKey: "engineBackPlanMissingFileReception" });
  if (isBlank(r?.validationBat)) out.push({ field: "validationBat", messageKey: "engineBackPlanMissingBatValidation" });
  return out;
}

/**
 * Do the three dates make sense in order: files ≤ proof approval ≤ delivery?
 * A warning only, never a block: a real rush can legitimately compress or invert the order.
 * @param r The back-planning dates.
 * @param deliveryDate The quote's delivery date.
 * @returns The warning message keys (translated by the display layer); empty when the order makes sense.
 */
export function backPlanningInconsistencies(
  r: BackPlanning | null | undefined,
  deliveryDate: DateISO | null | undefined,
): string[] {
  const out: string[] = [];
  const rf = r?.fileReception, bat = r?.validationBat;
  if (rf && bat && rf > bat) out.push("engineBackPlanFilesAfterBat");
  if (bat && deliveryDate && bat > deliveryDate) out.push("engineBackPlanBatAfterDelivery");
  if (rf && deliveryDate && rf > deliveryDate) out.push("engineBackPlanFilesAfterDelivery");
  return out;
}
